import { z } from "zod";
import {
  APPLICATION_STATUSES,
  BULK_MAX_ITEMS,
  MAX_TEXT_LONG,
  MAX_TEXT_SHORT,
} from "../constants.js";
import { paginationQuerySchema } from "./common.js";

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "日付は YYYY-MM-DD 形式で入力してください");

// 申込作成（学校）。日程は学校が指定（[要件] 企業は承認/却下のみ）。
export const applicationCreateInputSchema = z
  .object({
    companyId: z.string().min(1, "受入先企業を選択してください"),
    headcount: z.coerce.number().int().min(1, "人数は1以上で入力してください").max(1000),
    desiredJobType: z.string().trim().min(1, "希望職種は必須です").max(MAX_TEXT_SHORT),
    startDate: dateStr,
    endDate: dateStr,
    note: z.string().trim().max(MAX_TEXT_LONG).default(""),
  })
  .refine((v) => v.endDate >= v.startDate, {
    message: "終了日は開始日以降にしてください",
    path: ["endDate"],
  });
export type ApplicationCreateInput = z.infer<typeof applicationCreateInputSchema>;

// ── 一括申込（F-06b・基本設計 §4.3）──
// 共通は期間のみ。人数・希望職種・連絡事項は企業ごと（items）。開始日=今日以降はサーバで判定
// （TZの都合でここ=zod には置かない。request レベルでサービスが 400・§4.3 F-4）。
export const bulkApplicationInputSchema = z
  .object({
    startDate: dateStr,
    endDate: dateStr,
    // 必要な合計受入人数。選んだ各社の合計がこれを超えたら 400（自校の過剰申込を抑える・Q1-4）。
    targetHeadcount: z.coerce.number().int().min(1).max(10000),
    items: z
      .array(
        z.object({
          companyId: z.string().min(1),
          headcount: z.coerce.number().int().min(1, "人数は1以上").max(1000),
          desiredJobType: z.string().trim().min(1, "希望職種は必須です").max(MAX_TEXT_SHORT),
          note: z.string().trim().max(MAX_TEXT_LONG).default(""),
        }),
      )
      .min(1, "企業を1社以上選択してください")
      .max(BULK_MAX_ITEMS, `一度に申し込めるのは${BULK_MAX_ITEMS}社までです`),
  })
  .refine((v) => v.endDate >= v.startDate, {
    message: "終了日は開始日以降にしてください",
    path: ["endDate"],
  })
  .refine((v) => new Set(v.items.map((i) => i.companyId)).size === v.items.length, {
    message: "同じ企業が複数回選ばれています",
    path: ["items"],
  })
  .refine((v) => v.items.reduce((s, i) => s + i.headcount, 0) <= v.targetHeadcount, {
    message: "選択した企業の合計人数が必要な受入人数を超えています",
    path: ["targetHeadcount"],
  });
export type BulkApplicationInput = z.infer<typeof bulkApplicationInputSchema>;

// 一括申込の結果（企業名は返さない＝列挙防止。クライアントは選択済みで名前既知）。
export const BULK_OUTCOMES = ["created", "already_applied", "unavailable", "failed"] as const;
export type BulkOutcome = (typeof BULK_OUTCOMES)[number];
export const bulkApplicationResultSchema = z.object({
  results: z.array(z.object({ companyId: z.string(), outcome: z.enum(BULK_OUTCOMES) })),
  // secured=申込成立(created+already_applied) / notApplied=未申込(unavailable+failed)（§4.3 F-5）
  summary: z.object({ secured: z.number().int(), notApplied: z.number().int() }),
});
export type BulkApplicationResult = z.infer<typeof bulkApplicationResultSchema>;

// 却下（企業）
export const applicationRejectInputSchema = z.object({
  reason: z.string().trim().max(MAX_TEXT_LONG).optional(),
});
export type ApplicationRejectInput = z.infer<typeof applicationRejectInputSchema>;

// 取消（学校 or 確定後は企業）
export const applicationCancelInputSchema = z.object({
  reason: z.string().trim().max(MAX_TEXT_LONG).optional(),
});
export type ApplicationCancelInput = z.infer<typeof applicationCancelInputSchema>;

// 振り分け支援（F-08・最小）: 自校の申込を状態別に集計した「件数と合計受入人数」。
// 生徒個人情報は持たない＝人数のみ。承認済みの合計＝確保できた受入人数の目安（§4.4）。
const statusTotalSchema = z.object({
  count: z.number().int(),
  headcount: z.number().int(),
});
export const applicationSummarySchema = z.object({
  applied: statusTotalSchema,
  approved: statusTotalSchema,
  rejected: statusTotalSchema,
  cancelled: statusTotalSchema,
});
export type ApplicationSummary = z.infer<typeof applicationSummarySchema>;

// 一覧クエリ
export const applicationListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(APPLICATION_STATUSES).optional(),
  sort: z.enum(["created_at", "start_date"]).default("created_at"),
  order: z.enum(["asc", "desc"]).default("desc"),
});
export type ApplicationListQuery = z.infer<typeof applicationListQuerySchema>;

// 出力（allowlist）。表示に必要な相手方の名称も含む（生徒個人情報は含めない）。
export const applicationSchema = z.object({
  id: z.string(),
  schoolId: z.string(),
  schoolName: z.string(),
  companyId: z.string(),
  companyName: z.string(),
  companyCapacity: z.number().int(), // 宛先企業の受入可能人数（一度に受け入れられる上限）。企業の判断材料
  // 期間が重なる他申込の人数合計（この申込は含まない）。企業が承認時に capacity 超過を
  // 判断するための材料（警告表示・ブロックはしない。基本設計 §2.3）。企業ロールの受信箱で
  // のみ意味を持ち、他ロールでは 0。
  // - committedHeadcount: **承認済み**の重なり合計（確定した受入予定）
  // - pendingHeadcount:   **承認待ち(applied)** の他申込の重なり合計（保留中の見込み）。
  //   これが無いと、同時期に複数校が殺到して全部 applied のとき、各申込が「予定0」に見えて
  //   1件目の承認で警告が出ない（秋の殺到時に効かない）。
  committedHeadcount: z.number().int(),
  pendingHeadcount: z.number().int(),
  headcount: z.number().int(),
  desiredJobType: z.string(),
  startDate: z.string(), // YYYY-MM-DD
  endDate: z.string(),
  note: z.string(),
  status: z.enum(APPLICATION_STATUSES),
  rejectReason: z.string().nullable(),
  decidedAt: z.string().nullable(), // ISO8601 (UTC)
  // 企業へ未通知（§5）: status='applied' かつ通知メール未送信のまま15分経過。
  // 学校の申込一覧で警告＋再送導線を出す。company/admin でも同条件で計算される。
  notifiedPending: z.boolean(),
  createdAt: z.string(),
});
export type Application = z.infer<typeof applicationSchema>;
